import { useEffect, useRef, useState } from 'react';
import * as serviceWorkerRegistration from '../serviceWorkerRegistration';

/**
 * Shape of the config object accepted by serviceWorkerRegistration.register.
 * Modeled locally because ../serviceWorkerRegistration is still a loosely
 * typed .js module (migrated in a later milestone); only the members this
 * hook uses are declared here.
 */
interface ServiceWorkerRegisterConfig {
  onUpdate: (registration: ServiceWorkerRegistration) => void;
}

const register = serviceWorkerRegistration.register as (
  config?: ServiceWorkerRegisterConfig
) => void;

export interface ServiceWorkerUpdate {
  updateAvailable: boolean;
  applyUpdate: () => void;
}

/**
 * Registers the app's service worker and reports when a newly-deployed
 * build has been detected. No polling: the browser natively diffs
 * service-worker.js's bytes on page load, and those bytes change on every
 * deploy because scripts/generate-service-worker.js bakes the build's git
 * sha into the file (see scripts/service-worker.template.js).
 */
export function useServiceWorkerUpdate(): ServiceWorkerUpdate {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const waitingWorkerRef = useRef<ServiceWorker | null>(null);

  useEffect(() => {
    register({
      onUpdate: (registration: ServiceWorkerRegistration) => {
        if (!registration.waiting) return;
        waitingWorkerRef.current = registration.waiting;
        setUpdateAvailable(true);
      },
    });

    if (!('serviceWorker' in navigator)) {
      return undefined;
    }

    // Fires once the waiting worker (told to activate by applyUpdate below)
    // takes control of this page -- reload to run the new build.
    const handleControllerChange = () => window.location.reload();
    navigator.serviceWorker.addEventListener(
      'controllerchange',
      handleControllerChange
    );
    return () => {
      navigator.serviceWorker.removeEventListener(
        'controllerchange',
        handleControllerChange
      );
    };
  }, []);

  const applyUpdate = () => {
    waitingWorkerRef.current?.postMessage({ type: 'SKIP_WAITING' });
  };

  return { updateAvailable, applyUpdate };
}

export default useServiceWorkerUpdate;
